import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/auth.py LoginRequest / LoginResponse.
export interface LoginRequest {
  login_user: string;
  login_pass: string;
}

export interface LoginResponse {
  user_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  token: string | null;
  requires_2fa?: boolean;
  challenge?: string | null;
}

// The login endpoint wraps LoginResponse in the standard APIResponse envelope:
// {success, data: {user_id, ..., token}, error}. Verified against the live API.
export async function login(body: LoginRequest): Promise<LoginResponse> {
  const resp = await api.post<APIResponse<LoginResponse>>("/auth/login", body);
  return unwrap(resp.data);
}

// Mirrors app/routers/auth.py RegisterRequest. Returns the same LoginResponse
// (with a token) so the new account is signed in immediately.
export interface RegisterRequest {
  email: string;
  company?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  password: string;
}

export async function register(body: RegisterRequest): Promise<LoginResponse> {
  const resp = await api.post<APIResponse<LoginResponse>>("/auth/register", body);
  return unwrap(resp.data);
}

// --- Two-factor (TOTP) ---
export async function loginTwoFactor(challenge: string, code: string): Promise<LoginResponse> {
  const resp = await api.post<APIResponse<LoginResponse>>("/auth/login/2fa", { challenge, code });
  return unwrap(resp.data);
}

export async function twoFactorStatus(): Promise<{ enabled: boolean }> {
  const resp = await api.get<APIResponse<{ enabled: boolean }>>("/auth/2fa/status");
  return unwrap(resp.data);
}

export async function twoFactorSetup(): Promise<{ secret: string; otpauth_uri: string }> {
  const resp = await api.post<APIResponse<{ secret: string; otpauth_uri: string }>>("/auth/2fa/setup");
  return unwrap(resp.data);
}

export async function twoFactorEnable(code: string): Promise<void> {
  await api.post("/auth/2fa/enable", { code });
}

export async function twoFactorDisable(code: string): Promise<void> {
  await api.post("/auth/2fa/disable", { code });
}

export async function logout(): Promise<void> {
  try {
    await api.post("/auth/logout");
  } catch {
    // Logout is best-effort; clearing the local token is what matters.
  }
}

// ---------------------------------------------------------------------------
// Agent sign-in — a SEPARATE endpoint from the operator /login above
// (Resolved Decisions #2, 2026-07-30 agent-logins plan: agents get a
// separate branded /agent-login page and never use the operator form).
// Mirrors app/routers/auth.py's dedicated AgentLoginResponse — deliberately
// its own shape, not an extension of LoginResponse, so nothing about the
// operator response has to change for agents to sign in.
// ---------------------------------------------------------------------------

export interface SeatChoice {
  extension_id: number;
  name: string;
  title: string | null;
}

export interface AgentLoginResponse {
  principal: "agent";
  token: string | null;
  display_name: string | null;
  extension_id: number | null;
  // True for an unbound agent: no token yet, pick a seat from `seats` and
  // exchange `seat_challenge` via selectAgentSeat() below.
  requires_seat: boolean;
  seat_challenge: string | null;
  seats: SeatChoice[];
}

export async function loginAgent(body: LoginRequest): Promise<AgentLoginResponse> {
  const resp = await api.post<APIResponse<AgentLoginResponse>>("/auth/agent-login", body);
  return unwrap(resp.data);
}

// Second half of an unbound agent's sign-in: trade the seat_challenge (still
// live even after a 409 — the backend deliberately does not burn it on a
// claimed-seat conflict, so the caller can retry with a different seat) plus
// a chosen extension_id for a real token.
export async function selectAgentSeat(
  seatChallenge: string,
  extensionId: number,
): Promise<AgentLoginResponse> {
  const resp = await api.post<APIResponse<AgentLoginResponse>>("/auth/agent/seat", {
    seat_challenge: seatChallenge,
    extension_id: extensionId,
  });
  return unwrap(resp.data);
}
